<template>
    <div class="row justify-content-center">
        <div v-if="profile" class="account-container">
            <h2 class="title-text mx-auto">
                {{ $t('button.settings') }}
            </h2>

            <div class="row mx-0 mt-3 flex-md-row flex-column">
                <div class="col px-4 d-flex flex-column align-items-center">
                    <ValidationObserver ref="accountObserver">
                        <v-input
                            v-model="name"
                            class="input-w-default mb-0 mt-2"
                            ind="0"
                            type="text"
                            :placeholder="$t('placeholder.name')"
                            :icon="$getInputIcon(name, 'name')"
                            rules="required"
                            @input="accountEdited"
                        />

                        <v-input
                            v-model="surname"
                            class="input-w-default mb-0 mt-2"
                            ind="1"
                            type="text"
                            :placeholder="$t('placeholder.surname')"
                            :icon="$getInputIcon(surname, 'name')"
                            rules="required"
                            @input="accountEdited"
                        />

                        <v-input
                            v-if="teacher"
                            v-model="phone"
                            class="input-w-default mb-0 mt-2"
                            ind="2"
                            type="tel"
                            :placeholder="$t('placeholder.phone')"
                            :icon="$getInputIcon(phone, 'phone')"
                            rules="required|phone"
                            @input="accountEdited"
                        />

                        <v-input
                            v-if="teacher"
                            v-model="work"
                            class="input-w-default mb-0 mt-2"
                            ind="3"
                            type="text"
                            :placeholder="$t('placeholder.work')"
                            :icon="$getInputIcon(work, 'work')"
                            rules="required"
                            @input="accountEdited"
                        />

                        <v-input
                            v-if="teacher"
                            v-model="position"
                            class="input-w-default mb-0 mt-2"
                            ind="4"
                            type="text"
                            :placeholder="$t('placeholder.position')"
                            :icon="$getInputIcon(position, 'position')"
                            rules="required"
                            @input="accountEdited"
                        />
                    </ValidationObserver>

                    <ValidationObserver ref="mailObserver">
                        <v-input
                            v-model="mail"
                            class="input-w-default mb-0 mt-2"
                            ind="5"
                            type="text"
                            :placeholder="$t('placeholder.email')"
                            :icon="$getInputIcon(mail, 'email')"
                            :debounce="500"
                            rules="required|email|emailNotExists"
                            :disabled="profile.mailExists"
                        />

                        <b-button class="change-button" @click="changeMail">
                            {{ mailButtonText }}
                        </b-button>

                        <b-button class="change-button" to="/change-password" :disabled="!profile.mailExists">
                            {{ passwordButtonText }}
                        </b-button>
                    </ValidationObserver>
                </div>

                <div class="col px-4">
                    <b-button v-if="features.vkAuth" class="vk-button position-relative" style="margin-top: 51px;" @click="profile.mailExists ? updateVK() : validateMail()">
                        <img class="social-icon" src="../assets/images/socialIcons/vk.svg">

                        <div class="d-flex align-items-center ml-3">
                            {{ textVK }}
                        </div>
                    </b-button>

                    <confirm-modal ref="vkLinkedModalConfirm" :message="messageVkLinked" :description="$t('message.startBot')" use-no-button>
                        <template #yes-button>
                            <b-button class="button button-social-linked px-3 position-relative" @click="toVkNotificationBot">
                                <img class="position-absolute" style="left: 1rem; margin-top: 0.125rem" src="../assets/images/socialIcons/vk.svg">

                                <div class="social-text">
                                    {{ $t('button.go') }}
                                </div>
                            </b-button>
                        </template>
                    </confirm-modal>

                    <notify-settings />

                    <div v-if="features.vkAuth" class="justify-content-center">
                        <p class="text-white font-weight-light mb-2 text-notification">
                            {{ $t('message.subscribeNotifications') }}
                        </p>

                        <div class="row justify-content-center">
                            <template v-if="features.vkAuth">
                                <div id="disabled-vk-button">
                                    <b-button class="social-button position-relative" :href="vkBot" :disabled="!vkLinked" @click="updatePreferredMailingMethod($defines.MAILING_METHODS.VK)">
                                        <img src="../assets/images/socialIcons/vk.svg">
                                    </b-button>
                                </div>
    
                                <b-popover v-if="!vkLinked" target="disabled-vk-button" triggers="hover focus" placement="bottom">
                                    {{ $t('popover.subscribeNotifications', '', { social: $t('common.vk') }) }}
                                </b-popover>
                            </template>
                        </div>
                    </div>

                    <p class="text-white font-weight-light mb-2 text-type-select">
                        {{ $t('message.messagePriority') }}
                    </p>

                    <b-dropdown class="position-relative notify-type-combo" offset="25" toggle-class="mailing-dropdown-button" menu-class="mailing-dropdown-menu">
                        <template #button-content>
                            <div class="d-flex align-items-center">
                                <img class="ml-3" :src="notifyMethodSelectIcon" :class="preferredMailingMethod === $defines.MAILING_METHODS.NONE ? 'icon-no-notify' : 'icon'">

                                <div class="ml-3">
                                    {{ notificationTypeLabel }}
                                </div>

                                <img class="ml-auto" :src="require('images/headerIcons/arrow-white.svg')">
                            </div>
                        </template>

                        <b-dropdown-item v-if="emailExists" class="mailing-dropdown-item-last header-text item-substrate" @click="updatePreferredMailingMethod($defines.MAILING_METHODS.EMAIL)">
                            <img class="icon ml-1" src="../assets/images/formIcons/email-fill.svg">

                            <div class="ml-3">
                                E-mail
                            </div>

                            <img v-if="preferredMailingMethod === $defines.MAILING_METHODS.EMAIL" class="ml-auto mr-n3" :src="require('images/headerIcons/tick.svg')">
                        </b-dropdown-item>

                        <b-dropdown-item v-if="vkLinked && features.vkAuth" class="mailing-dropdown-item header-text item-substrate" @click="updatePreferredMailingMethod($defines.MAILING_METHODS.VK)">
                            <img class="icon ml-1" src="../assets/images/socialIcons/vk.svg">

                            <div class="mx-3">
                                VK
                            </div>

                            <img v-if="preferredMailingMethod === $defines.MAILING_METHODS.VK" class="ml-auto mr-n3" :src="require('images/headerIcons/tick.svg')">
                        </b-dropdown-item>

                        <b-dropdown-item class="mailing-dropdown-item-first header-text item-substrate" @click="updatePreferredMailingMethod($defines.MAILING_METHODS.NONE)">
                            <img class="ml-1" src="../assets/images/headerIcons/cross.svg">

                            <div class="no-notify-text">
                                {{ $t('label.noNotify') }}
                            </div>

                            <img v-if="preferredMailingMethod === $defines.MAILING_METHODS.NONE" class="ml-auto mr-n3" :src="require('images/headerIcons/tick.svg')">
                        </b-dropdown-item>
                    </b-dropdown>
                </div>
            </div>

            <div v-if="mireraTeacher" class="flex-wrap">
                <small class="hint-text">{{ $t('message.refuseMireraTeacher') }}<a class="text-link" href="#0" @click="confirmAnnulAccountType">{{ $t('button.clickHere') }}
                    <confirm-modal ref="modalConfirm" :message="$t('message.confirmRefuseMireraTeacher')" use-no-button @confirmed="annulAccountType" /></a></small>
            </div>
        </div>
    </div>
</template>

<script>
    import ConfirmModal from './ConfirmModal';
    import VInput from './VInput';
    import api from '@/api';
    import { USER_UPDATE_MAIL } from 'actions/user';
    import { USER_UPDATE } from 'mutations/user';
    import Paths from './mixins/Paths';
    import UrlUtils from './mixins/UrlUtils';
    import { mapGetters } from 'vuex';
    import NotifySettings from  './NotifySettings.vue';
    import Queries from './mixins/Queries';

    export default {
        name: 'Account',

        components: {
            NotifySettings,
            VInput,
            ConfirmModal
        },

        mixins: [
            Paths,
            UrlUtils,
            Queries
        ],

        computed: {
            ...mapGetters('user', [ 'profile', 'status' ]),
            
            ...mapGetters([ 'features' ]),

            name: {
                get() {
                    return this.profile.name;
                },
                set(value) {
                    this.$store.commit(`user/${USER_UPDATE}`, { name: value });
                }
            },

            surname: {
                get() {
                    return this.profile.surname;
                },
                set(value) {
                    this.$store.commit(`user/${USER_UPDATE}`, { surname: value });
                }
            },

            phone: {
                get() {
                    return this.profile.phone;
                },
                set(value) {
                    this.$store.commit(`user/${USER_UPDATE}`, { phone: value });
                }
            },

            work: {
                get() {
                    return this.profile.work;
                },
                set(value) {
                    this.$store.commit(`user/${USER_UPDATE}`, { work: value });
                }
            },

            position: {
                get() {
                    return this.profile.position;
                },
                set(value) {
                    this.$store.commit(`user/${USER_UPDATE}`, { position: value });
                }
            },

            preferredMailingMethod: {
                get() {
                    return this.profile.preferredMailingMethod;
                },
                set(value) {
                    this.$store.commit(`user/${USER_UPDATE}`, { preferredMailingMethod: value });
                }
            },

            vkId: {
                get() {
                    return this.profile.vkId;
                },
                set(value) {
                    this.$store.commit(`user/${USER_UPDATE}`, { vkId: value });
                }
            },

            teacher() {
                let permissions = this.profile.permissions;
                let TEACHER = this.$defines.SERVICE_ACCOUNT_TYPES.TEACHER;
                return permissions.mirera === TEACHER || permissions.pm === TEACHER;
            },

            notificationTypeLabel() {
                switch (this.preferredMailingMethod) {
                    case this.$defines.MAILING_METHODS.NONE:
                        return this.$t('label.noNotify');
                    case this.$defines.MAILING_METHODS.VK:
                        return 'VK';
                    case this.$defines.MAILING_METHODS.EMAIL:
                        return 'E-mail';
                    default:
                        return '';
                }
            },

            mireraTeacher() {
                return this.profile.permissions.mirera === this.$defines.SERVICE_ACCOUNT_TYPES.TEACHER;
            },

            textVK() {
                return this.vkId === '' ? this.$t('button.linkTo', '', { social: this.$t('common.vk') }) : this.$t('button.unlinkFrom', '', { social: this.$t('common.vk') });
            },

            vkLinked() {
                return this.vkId !== '';
            },

            emailExists() {
                return this.profile.mail !== '';
            },

            passwordButtonText() {
                return this.profile.passwordExists ? this.$t('button.changePassword') : this.$t('button.createPassword');
            },

            mailButtonText() {
                return this.profile.mailExists ? this.$t('button.changeEmail') : this.$t('button.saveEmail');
            },

            messageVkLinked() {
                return this.vkLink ? this.$t('message.successLink', '', { social: this.$t('common.vk') }) : this.$t('message.successRegistration');
            },

            notifyMethodSelectIcon() {
                switch (this.preferredMailingMethod) {
                    case this.$defines.MAILING_METHODS.VK:
                        return require('images/socialIcons/vk.svg');
                    case this.$defines.MAILING_METHODS.EMAIL:
                        return require('images/formIcons/email-fill.svg');
                    default:
                        return require('images/headerIcons/cross.svg');
                }
            }
        },

        data() {
            return {
                mail: '',
                vkLink: false,
                showModal: false,
                redirectUri: '/account'
            };
        },

        watch: {
            status(data) {
                if (data === this.$defines.AUTHORIZE_STATUS.AUTHORIZED)
                    this.checkQueryAndMail();
            }
        },

        created() {
            if (this.status === this.$defines.AUTHORIZE_STATUS.AUTHORIZED)
                this.checkQueryAndMail();
        },

        methods: {
            checkQueryAndMail() {
                this.$nextTick(() => {
                    if (this.profile && this.profile.mailExists)
                        this.mail = this.profile.mail;

                    if (this.$route.query.alreadyRegistered) {
                        this.$toast.warning(this.$t('toast.alreadyRegistered'), '', { position: 'topRight' });

                        this.$router.replace({ query: { ...this.$route.query, alreadyRegistered: undefined } });
                    }

                    if (this.$route.query.passwordChanged === 'true') {
                        this.$toast.success(this.$t('toast.passwordChanged'), '', { position: 'topRight' });

                        let newQuery = { ...this.$route.query };
                        delete newQuery.passwordChanged;
                        this.$router.replace({ 'query': newQuery });
                    }

                    if (this.$route.query.mailChanged === 'true') {
                        this.$toast.success(this.$t('toast.mailChanged'), '', { position: 'topRight' });
                        this.$router.replace({ 'query': null });
                    }

                    if (this.$route.query.vkIsLinked === 'true') {
                        let newQuery = { ...this.$route.query };

                        if (this.$route.query.vkFlow === this.$defines.FLOW_TYPES.LINK)
                            this.vkLink = true;
                        else
                            newQuery.redirectWebsites = this.mireraPath;

                        delete newQuery.vkIsLinked;
                        delete newQuery.vkFlow;
                        this.$router.replace({ 'query': newQuery });

                        this.$refs.vkLinkedModalConfirm.showModal();
                    }
                });
            },

            validateMail() {
                if (!this.profile.mailExists) {
                    this.$refs.mailObserver.validate();
                    return false;
                }

                return true;
            },

            async updatePreferredMailingMethod( method ) {
                this.preferredMailingMethod = method;

                await this.accountEdited();
            },

            async accountEdited() {
                this.$nextTick(async () => {
                    let isValid = await this.$refs.accountObserver.validate();

                    if (!isValid)
                        return;

                    await api.post('/app/account', {
                        name: this.name,
                        surname: this.surname,
                        phone: this.phone,
                        work: this.work,
                        position: this.position,
                        preferredMailingMethod: this.preferredMailingMethod,
                        acceptedMailing: this.preferredMailingMethod !== this.$defines.MAILING_METHODS.NONE
                    });
                });
            },

            async changeMail() {
                let isValid = await this.$refs.mailObserver.validate();

                if (this.profile.mailExists)
                    this.openRelativeUrl('/change-mail', false);

                if (!isValid)
                    return;

                try {
                    let response = await this.$store.dispatch(`user/${USER_UPDATE_MAIL}`, this.mail);

                    if (response && response.status === this.$defines.RESPONSE_STATUS.OK)
                        this.$toast.success(this.mail, this.$t('message.confirmLetterForEmailChangeSent'), { position: 'topRight' });
                }
                catch (error) {
                    this.$toast.error(this.$t('message.mailChangeFailed'), '', { position: 'topRight' });
                }
            },

            async updateVK() {
                if (this.vkId === '') {
                    let url = `/app/authorize/vk?flow=${this.$defines.FLOW_TYPES.LINK}&redirect_uri=${this.redirectUri}`;

                    if (this.$route.query.redirectWebsites)
                        url += `?redirectWebsites=${this.$route.query.redirectWebsites}`;

                    window.location.href = url;
                }
                else {
                    let responseUnlink = await api.get('/app/account/vk/unlink');

                    if (responseUnlink && responseUnlink.status === this.$defines.RESPONSE_STATUS.OK) {
                        this.vkId = '';

                        if (this.preferredMailingMethod === this.$defines.MAILING_METHODS.VK)
                            await this.updatePreferredMailingMethod(this.$defines.MAILING_METHODS.NONE);
                    }
                }
            },

            confirmAnnulAccountType() {
                this.$nextTick(() => {
                    this.$refs.modalConfirm.showModal();
                });
            },

            async annulAccountType() {
                this.$store.commit(`user/${USER_UPDATE}`, {
                    permissions: {
                        'mirera': this.$defines.SERVICE_ACCOUNT_TYPES.USER,
                        'pm': this.profile.permissions.pm
                    }
                });
                await api.post('/app/account/permissions/mirera/annul');
            },

            async toVkNotificationBot() {
                this.$refs.vkLinkedModalConfirm.reject();

                await this.updatePreferredMailingMethod(this.$defines.MAILING_METHODS.VK);

                this.openUrl(this.vkBot, false, true);
            }
        }
    };
</script>

<style>
    .button-social-linked {
        background-color: #29ABE2 !important;
        border: 1px solid #29ABE2 !important;
    }

    .button-social-linked:hover {
        background-color: #338DC9 !important;
    }

    .social-text {
        margin-left: 1.75rem;
        margin-right: 0.5rem;
    }
</style>

<style scoped>
    .title-text {
        font-weight: lighter !important;
        font-size: 23px;
        line-height: 27px;
        text-decoration: none !important;
        padding-bottom: 2px;
        border-bottom: 1px solid #FFFFFF;
        color: white;
        text-align: center;
        text-decoration-skip-ink: none;
        width: 40% !important;
    }

    .vk-button,
    .change-button,
    .notify-type-combo {
        width: 220px !important;
        margin-left: auto !important;
        margin-right: auto !important;
    }

    .social-button {
        width: 37px !important;
        height: 29px !important;
    }

    .change-button {
        margin-top: 32px;
    }

    .notify-type-combo {
        display: flex !important;
        flex-direction: row !important;
        justify-content: center !important;
        transition: ease-in-out;
        align-items: center;
        border-radius: 5px !important;
        margin-top: 6px;
    }

    .vk-button,
    .social-button,
    .change-button {
        background-color: transparent !important;
        border: 1px solid #338DC9 !important;
        border-radius: 5px !important;
        display: flex !important;
        flex-direction: row !important;
        justify-content: center !important;
        transition: ease-in-out;
        align-items: center;
    }

    .change-button:hover,
    .social-button:hover,
    .vk-button:hover {
        background-color: #29ABE2 !important;
    }

    .hint-text {
        display: block;
        color: white;
        text-align: center;
        opacity: 0.6;
        margin-top: 32px;
    }

    .hint-text:hover {
        opacity: 1;
    }

    .text-link {
        color: #29ABE2 !important;
    }

    .text-notification {
        font-size: 15px;
        text-align: center;
        margin-top: 25px;
    }

    .text-type-select {
        font-size: 15px;
        text-align: center;
        margin-top: 25px;
    }

    .social-icon {
        left: 25%;
        margin-left: -1.5rem !important;
        position: absolute !important;
        height: 16px;
        width: 16px;
    }

    @media screen and (max-width: 767px) {
        .account-container {
            width: 100% !important;
        }

        .account-container button, a {
            margin-top: 32px !important;
        }

        .social-button {
            margin-top: 0.5rem !important;
        }

        .hint-text {
            margin-top: 25px;
        }

        .notify-type-combo {
            margin-bottom: 125px !important;
        }
    }

    @media screen and (max-height: 728px) {
        .notify-type-combo {
            margin-bottom: 150px !important;
        }
    }

    @media screen and (min-width: 767px) {
        .account-container {
            width: 700px !important;
        }
    }

    @media screen and (min-width: 400px) {
        .title-text {
            width: 25%;
        }
    }

    /deep/ .mailing-dropdown-button::after {
        content: none !important;
    }

    /deep/ .mailing-dropdown-button {
        width: 220px !important;
        height: 38px !important;
        background-color: transparent !important;
        outline: none !important;
        box-shadow: none !important;
        border: 1px solid #338DC9 !important;
    }

    /deep/ .b-dropdown > .dropdown-toggle > img:not(.social-icon) {
        transition: .3s ease all
    }

    /deep/ .b-dropdown.show > .dropdown-toggle > img:not(.social-icon) {
        transform: rotate(180deg);
    }

    /deep/ .mailing-dropdown-menu {
        padding: 0 !important;
        margin-top: 6px !important;
        transform: translate3d(0, 2.5rem, 0) !important;
        background: transparent !important;
        width: 220px !important;
        border: 1px solid #338DC9 !important;
        overflow: hidden;
        position: absolute !important;
        margin-left: auto !important;
        margin-right: auto !important;
        will-change: transform !important;
    }

    /deep/ .mailing-dropdown-menu.show {
        max-height: 150px !important;
        border: 1px solid #338DC9 !important;
    }

    /deep/ .dropdown-item {
        overflow: hidden;
        text-align: start;
        color:white;
        background: transparent !important;
        border-bottom: 1px solid transparent !important;
        border-top: 1px solid transparent !important;
    }

    /deep/ .dropdown-item:hover {
      overflow: hidden;
      text-align: start;
      color:white;
      background: #338DC9  !important;
    }

    /deep/ .mailing-dropdown-item a,
    /deep/ .mailing-dropdown-item-first a,
    /deep/ .mailing-dropdown-item-last a {
        display: flex;
        align-items: center;
        height: 32px;
    }

    /deep/ .mailing-dropdown-item:hover,
    /deep/ .mailing-dropdown-item-first:hover,
    /deep/ .mailing-dropdown-item-last:hover {
        background-color: transparent !important;
    }

    /deep/ .mailing-dropdown-item:active a {
        background-color: transparent !important;
        color: white !important;
    }

    /deep/ .dropdown-item:active {
        background-color: transparent !important;
        color: white !important;
    }

    /deep/ .b-dropdown > .dropdown-toggle > img:not(.social-icon) {
        transition: .3s ease all
    }

    /deep/ .b-dropdown.show > .dropdown-toggle > img:not(.social-icon) {
        transform: rotate(180deg);
    }

    .icon {
        height: 16px;
        width: 16px;
    }

    .item-substrate {
        cursor: pointer;
        height: 32px;
    }

    .item-substrate:hover,
    .item-substrate:active {
        background-color: transparent !important;
    }

    .header-text {
        font-size: 1rem;
        color: white !important;
    }

    .no-notify-text {
        margin-left: 14px;
        vertical-align: center;
    }

    .icon-no-notify {
        height: 18px;
        width: 18px;
    }
</style>
